// TestReact.js
import React, { useState } from "react";
import {
  Box,
  Button,
  Heading,
  Text,
  VStack,
  HStack,
  Container,
  Progress,
  Badge,
  Code,
} from "@chakra-ui/react";

import SendTestResults from "./SendTestResults";

const quizQuestions = [
  // =========================
  // EASY QUESTIONS (10)
  // =========================
  {
    questionText: "What is JSX?",
    answerOptions: [
      { answerText: "A JavaScript framework", isCorrect: false },
      { answerText: "A syntax extension for JavaScript", isCorrect: true },
      { answerText: "A styling library", isCorrect: false },
      { answerText: "A database query language", isCorrect: false },
    ],
    explanation:
      "JSX is a syntax extension that lets you write HTML-like code inside JavaScript. React compiles it into `React.createElement` calls.",
  },
  {
    questionText: "Which command creates a new React app?",
    answerOptions: [
      { answerText: "npm create-react-app", isCorrect: false },
      { answerText: "npx create-react-app my-app", isCorrect: true },
      { answerText: "react new-app", isCorrect: false },
      { answerText: "yarn start new-app", isCorrect: false },
    ],
    explanation:
      "`npx create-react-app my-app` is the official command. `npx` ensures you use the latest version without global installs.",
  },
  {
    questionText: "React is mainly used for?",
    answerOptions: [
      { answerText: "Backend development", isCorrect: false },
      { answerText: "UI development", isCorrect: true },
      { answerText: "Database management", isCorrect: false },
      { answerText: "Server configuration", isCorrect: false },
    ],
    explanation: "React is a front-end library used for building user interfaces.",
  },
  {
    questionText: "What is the default state of a React component if not set?",
    answerOptions: [
      { answerText: "null", isCorrect: true },
      { answerText: "undefined", isCorrect: false },
      { answerText: "0", isCorrect: false },
      { answerText: "empty string", isCorrect: false },
    ],
    explanation:
      "If you don’t define initial state in a React component, it defaults to `null`.",
  },
  {
    questionText: "Which method is used to update state in React?",
    answerOptions: [
      { answerText: "setState()", isCorrect: true },
      { answerText: "updateState()", isCorrect: false },
      { answerText: "changeState()", isCorrect: false },
      { answerText: "modifyState()", isCorrect: false },
    ],
    explanation: "React state is updated using the `setState` method in class components or the updater function in hooks.",
  },
  {
    questionText: "Which hook is used for side effects in functional components?",
    answerOptions: [
      { answerText: "useState", isCorrect: false },
      { answerText: "useEffect", isCorrect: true },
      { answerText: "useContext", isCorrect: false },
      { answerText: "useMemo", isCorrect: false },
    ],
    explanation:
      "`useEffect` is used for side effects like fetching data, subscriptions, or manually changing the DOM.",
  },
  {
    questionText: "Which of these is a correct way to write a React functional component?",
    answerOptions: [
      {
        answerText: "function MyComponent() { return <div>Hello</div> }",
        isCorrect: true,
      },
      { answerText: "component MyComponent() { return <div>Hello</div> }", isCorrect: false },
      { answerText: "def MyComponent() { return <div>Hello</div> }", isCorrect: false },
      { answerText: "class MyComponent extends React { render() {} }", isCorrect: false },
    ],
    explanation: "Functional components are defined using `function` keyword and return JSX.",
  },
  {
    questionText: "Which prop is used to pass data from parent to child?",
    answerOptions: [
      { answerText: "state", isCorrect: false },
      { answerText: "props", isCorrect: true },
      { answerText: "context", isCorrect: false },
      { answerText: "ref", isCorrect: false },
    ],
    explanation: "Props are used to pass data from parent to child components.",
  },
  {
    questionText: "Which of these is used for conditional rendering?",
    answerOptions: [
      { answerText: "if statements inside JSX", isCorrect: false },
      { answerText: "Ternary operator", isCorrect: true },
      { answerText: "for loops inside JSX", isCorrect: false },
      { answerText: "None of the above", isCorrect: false },
    ],
    explanation:
      "React supports conditional rendering using ternary operators or logical && inside JSX.",
  },
  {
    questionText: "Which function renders React elements to the DOM?",
    answerOptions: [
      { answerText: "React.render()", isCorrect: false },
      { answerText: "ReactDOM.render()", isCorrect: true },
      { answerText: "renderDOM()", isCorrect: false },
      { answerText: "ReactDOM.create()", isCorrect: false },
    ],
    explanation: "ReactDOM.render() renders React elements to the specified DOM node.",
  },

  // =========================
  // MEDIUM QUESTIONS (20)
  // =========================
  {
    questionText: "What will this code log?",
    codeSnippet: `
function App() {
  const [count, setCount] = React.useState(0);
  setCount(count + 1);
  console.log(count);
  return <div>{count}</div>;
}
    `,
    answerOptions: [
      { answerText: "It will keep logging 0 (infinite loop)", isCorrect: true },
      { answerText: "It will log 1 once", isCorrect: false },
      { answerText: "It will crash immediately", isCorrect: false },
      { answerText: "It will log 0,1,2 sequentially", isCorrect: false },
    ],
    explanation:
      "Calling `setCount` directly in render triggers infinite re-renders. React throws an error (`Too many re-renders`).",
  },
  {
    questionText: "What will the output of this component be?",
    codeSnippet: `
function Greeting({ name }) {
  return <h1>Hello {name || 'Guest'}</h1>;
}
<Greeting name={0} />
    `,
    answerOptions: [
      { answerText: "Hello Guest", isCorrect: false },
      { answerText: "Hello 0", isCorrect: true },
      { answerText: "Hello undefined", isCorrect: false },
      { answerText: "Throws error", isCorrect: false },
    ],
    explanation:
      "In JavaScript, 0 is falsy but not null/undefined. So the logical OR fallback is ignored and 0 is rendered.",
  },
  {
    questionText: "Which of the following correctly adds a click handler?",
    codeSnippet: `
function App() {
  return <button onClick={alert('Clicked!')}>Click</button>
}
    `,
    answerOptions: [
      { answerText: "Correct as is", isCorrect: false },
      {
        answerText: "Use onClick={() => alert('Clicked!')}",
        isCorrect: true,
      },
      { answerText: "Use onClick=alert('Clicked!')", isCorrect: false },
      { answerText: "Use onClick: alert('Clicked!')", isCorrect: false },
    ],
    explanation:
      "The function should be passed as a reference or inline arrow function. Otherwise it runs immediately.",
  },
  {
    questionText: "What is the key purpose of a 'key' prop in lists?",
    answerOptions: [
      { answerText: "Add styling to list elements", isCorrect: false },
      { answerText: "Identify elements to optimize re-rendering", isCorrect: true },
      { answerText: "Assign default values", isCorrect: false },
      { answerText: "Link child to parent state", isCorrect: false },
    ],
    explanation: "'key' helps React identify which items changed, added, or removed.",
  },
  {
    questionText: "What will this code log?",
    codeSnippet: `
const [state, setState] = React.useState({ count: 0 });
setState({ count: state.count + 1 });
console.log(state.count);
    `,
    answerOptions: [
      { answerText: "1", isCorrect: false },
      { answerText: "0", isCorrect: true },
      { answerText: "2", isCorrect: false },
      { answerText: "Undefined", isCorrect: false },
    ],
    explanation:
      "State updates are asynchronous. Logging immediately shows the previous value.",
  },
  {
    questionText: "How can you prevent re-rendering of a functional component?",
    answerOptions: [
      { answerText: "useMemo / React.memo", isCorrect: true },
      { answerText: "setState(null)", isCorrect: false },
      { answerText: "useEffect(() => {}, [])", isCorrect: false },
      { answerText: "Wrapping in Fragment", isCorrect: false },
    ],
    explanation:
      "`React.memo` and `useMemo` help prevent unnecessary re-renders by memoizing components or values.",
  },
  {
    questionText: "What will this code output?",
    codeSnippet: `
const numbers = [1, 2, 3];
const double = numbers.map(n => n * 2);
console.log(double);
    `,
    answerOptions: [
      { answerText: "[2,4,6]", isCorrect: true },
      { answerText: "[1,2,3]", isCorrect: false },
      { answerText: "[1,4,9]", isCorrect: false },
      { answerText: "[2,3,4]", isCorrect: false },
    ],
    explanation: "`map` transforms each element and returns a new array.",
  },
  {
    questionText: "How do you lift state up in React?",
    answerOptions: [
      {
        answerText: "Move the state to the closest common ancestor",
        isCorrect: true,
      },
      { answerText: "Use Redux only", isCorrect: false },
      { answerText: "Use a useEffect hook", isCorrect: false },
      { answerText: "Store state in localStorage", isCorrect: false },
    ],
    explanation:
      "Lifting state up means moving state to the nearest common ancestor to share between children.",
  },
  {
    questionText: "What is the default behavior of form submit in React?",
    answerOptions: [
      { answerText: "Page reloads", isCorrect: true },
      { answerText: "Prevents default automatically", isCorrect: false },
      { answerText: "Logs values to console", isCorrect: false },
      { answerText: "Nothing happens", isCorrect: false },
    ],
    explanation: "HTML forms reload the page by default unless preventDefault is called.",
  },
  {
    questionText: "What will this code output?",
    codeSnippet: `
const arr = [1,2,3];
const result = arr.filter(n => n > 2);
console.log(result);
    `,
    answerOptions: [
      { answerText: "[3]", isCorrect: true },
      { answerText: "[1,2,3]", isCorrect: false },
      { answerText: "[2,3]", isCorrect: false },
      { answerText: "[]", isCorrect: false },
    ],
    explanation: "`filter` returns all elements that satisfy the condition.",
  },
  {
    questionText: "Which hook is best for referencing DOM elements?",
    answerOptions: [
      { answerText: "useRef", isCorrect: true },
      { answerText: "useState", isCorrect: false },
      { answerText: "useEffect", isCorrect: false },
      { answerText: "useCallback", isCorrect: false },
    ],
    explanation: "`useRef` provides a mutable reference to DOM elements or values that persists across renders.",
  },
  {
    questionText: "What will this log?",
    codeSnippet: `
const obj = { a: 1 };
const copy = { ...obj, b: 2 };
console.log(copy);
    `,
    answerOptions: [
      { answerText: "{ a:1, b:2 }", isCorrect: true },
      { answerText: "{ a:1 }", isCorrect: false },
      { answerText: "{ b:2 }", isCorrect: false },
      { answerText: "{ a:2, b:1 }", isCorrect: false },
    ],
    explanation: "The spread operator copies existing object properties and adds/overrides new ones.",
  },
  {
    questionText: "Which hook should you use to memoize expensive calculations?",
    answerOptions: [
      { answerText: "useMemo", isCorrect: true },
      { answerText: "useEffect", isCorrect: false },
      { answerText: "useCallback", isCorrect: false },
      { answerText: "useState", isCorrect: false },
    ],
    explanation:
      "`useMemo` memoizes a calculation so it only recalculates when dependencies change.",
  },
  {
    questionText: "What will this render?",
    codeSnippet: `
const element = <div>{['a','b','c'].map(x => x)}</div>;
ReactDOM.render(element, document.getElementById('root'));
    `,
    answerOptions: [
      { answerText: "<div>abc</div>", isCorrect: true },
      { answerText: "<div>a,b,c</div>", isCorrect: false },
      { answerText: "<div>[a,b,c]</div>", isCorrect: false },
      { answerText: "<div>['a','b','c']</div>", isCorrect: false },
    ],
    explanation: "Mapping over an array returns elements; commas aren’t inserted in JSX output.",
  },
  {
    questionText: "Which is true about controlled components?",
    answerOptions: [
      { answerText: "Input value is managed by state", isCorrect: true },
      { answerText: "Input value is uncontrolled", isCorrect: false },
      { answerText: "Cannot be used with forms", isCorrect: false },
      { answerText: "Managed by props only", isCorrect: false },
    ],
    explanation:
      "Controlled components get their value from React state, allowing React to control form elements.",
  },
  {
    questionText: "What does this component return?",
    codeSnippet: `
function MyComponent() {
  return null;
}
    `,
    answerOptions: [
      { answerText: "Renders nothing", isCorrect: true },
      { answerText: "Throws error", isCorrect: false },
      { answerText: "<div></div>", isCorrect: false },
      { answerText: "Undefined", isCorrect: false },
    ],
    explanation: "Returning `null` in React renders nothing to the DOM.",
  },
  {
    questionText: "Which lifecycle is replaced by useEffect in functional components?",
    answerOptions: [
      { answerText: "componentDidMount / componentDidUpdate", isCorrect: true },
      { answerText: "render only", isCorrect: false },
      { answerText: "constructor", isCorrect: false },
      { answerText: "shouldComponentUpdate", isCorrect: false },
    ],
    explanation: "useEffect can handle mounting, updating, and cleanup, replacing several class lifecycle methods.",
  },

  // =========================
  // HARD QUESTIONS (10)
  // =========================
  {
    questionText: "What does React Fiber enable?",
    answerOptions: [
      {
        answerText: "Incremental rendering with the ability to pause work",
        isCorrect: true,
      },
      { answerText: "Server-side rendering", isCorrect: false },
      { answerText: "Automatic state persistence", isCorrect: false },
      { answerText: "Better CSS handling", isCorrect: false },
    ],
    explanation:
      "React Fiber is the new reconciliation engine. It allows React to break rendering into chunks and prioritize updates.",
  },
  {
    questionText: "What is the purpose of useCallback?",
    answerOptions: [
      { answerText: "To memoize functions between renders", isCorrect: true },
      { answerText: "To handle side effects", isCorrect: false },
      { answerText: "To create refs", isCorrect: false },
      { answerText: "To store state", isCorrect: false },
    ],
    explanation:
      "`useCallback` returns a memoized callback that only changes if dependencies change, useful for optimizing child components.",
  },
  {
    questionText: "What will this code output?",
    codeSnippet: `
const [count, setCount] = React.useState(0);
const increment = () => setCount(c => c + 1);
increment();
increment();
console.log(count);
    `,
    answerOptions: [
      { answerText: "0", isCorrect: true },
      { answerText: "2", isCorrect: false },
      { answerText: "1", isCorrect: false },
      { answerText: "Error", isCorrect: false },
    ],
    explanation:
      "State updates are asynchronous; logging immediately shows the old state (0).",
  },
  {
    questionText: "What will this render?",
    codeSnippet: `
function App() {
  const [val, setVal] = React.useState(0);
  return (
    <div>
      {val && <span>Yes</span>}
    </div>
  );
}
    `,
    answerOptions: [
      { answerText: "<div></div>", isCorrect: true },
      { answerText: "<div><span>Yes</span></div>", isCorrect: false },
      { answerText: "<div>0</div>", isCorrect: false },
      { answerText: "<span>Yes</span>", isCorrect: false },
    ],
    explanation:
      "0 is falsy; using `&&` will not render the `<span>` element when val is 0.",
  },
  {
    questionText: "What is the difference between React.memo and useMemo?",
    answerOptions: [
      { answerText: "React.memo memoizes components, useMemo memoizes values", isCorrect: true },
      { answerText: "React.memo memoizes values, useMemo memoizes components", isCorrect: false },
      { answerText: "Both memoize components", isCorrect: false },
      { answerText: "Both memoize values", isCorrect: false },
    ],
    explanation:
      "React.memo prevents unnecessary component re-renders; useMemo caches expensive values between renders.",
  },
  {
    questionText: "What will this code log?",
    codeSnippet: `
const [data, setData] = React.useState([]);
React.useEffect(() => {
  setData([1,2,3]);
}, []);
console.log(data);
    `,
    answerOptions: [
      { answerText: "[]", isCorrect: true },
      { answerText: "[1,2,3]", isCorrect: false },
      { answerText: "undefined", isCorrect: false },
      { answerText: "Error", isCorrect: false },
    ],
    explanation:
      "useEffect runs after render. Logging immediately inside render shows initial state (empty array).",
  },
  {
    questionText: "Which hook would you use for an expensive computation that needs caching?",
    answerOptions: [
      { answerText: "useMemo", isCorrect: true },
      { answerText: "useEffect", isCorrect: false },
      { answerText: "useCallback", isCorrect: false },
      { answerText: "useState", isCorrect: false },
    ],
    explanation:
      "useMemo memoizes values to avoid recalculating expensive computations on each render.",
  },
  {
    questionText: "What will this render?",
    codeSnippet: `
const [flag, setFlag] = React.useState(true);
return <>{flag || <span>No</span>}</>;
    `,
    answerOptions: [
      { answerText: "true", isCorrect: true },
      { answerText: "<span>No</span>", isCorrect: false },
      { answerText: "false", isCorrect: false },
      { answerText: "<div>No</div>", isCorrect: false },
    ],
    explanation:
      "flag is true, so using `||` operator returns the first truthy value: true (renders as string).",
  },
  {
    questionText: "Why does React batch state updates?",
    answerOptions: [
      { answerText: "To optimize performance by minimizing re-renders", isCorrect: true },
      { answerText: "To preserve previous state", isCorrect: false },
      { answerText: "To delay effects", isCorrect: false },
      { answerText: "To avoid memory leaks", isCorrect: false },
    ],
    explanation:
      "React batches multiple state updates into a single render to optimize performance.",
  },
  {
    questionText: "What is the difference between useLayoutEffect and useEffect?",
    answerOptions: [
      { answerText: "useLayoutEffect runs synchronously after DOM mutations", isCorrect: true },
      { answerText: "useEffect runs synchronously", isCorrect: false },
      { answerText: "No difference", isCorrect: false },
      { answerText: "useLayoutEffect is for server side only", isCorrect: false },
    ],
    explanation:
      "useLayoutEffect runs before the browser paints, useEffect runs after painting, which affects visual updates.",
  },
  {
    questionText: "What will this code log?",
    codeSnippet: `
const [count, setCount] = React.useState(0);
setCount(prev => prev + 1);
setCount(prev => prev + 1);
console.log(count);
    `,
    answerOptions: [
      { answerText: "0", isCorrect: true },
      { answerText: "2", isCorrect: false },
      { answerText: "1", isCorrect: false },
      { answerText: "Error", isCorrect: false },
    ],
    explanation:
      "Using functional updates ensures correct state, but logging immediately shows old value (0) due to async updates.",
  },
  {
    questionText: "Which statement about Context API is correct?",
    answerOptions: [
      { answerText: "It allows passing data through the component tree without props", isCorrect: true },
      { answerText: "It replaces useState", isCorrect: false },
      { answerText: "It cannot be used with functional components", isCorrect: false },
      { answerText: "It forces re-render of all components", isCorrect: false },
    ],
    explanation:
      "Context allows sharing data globally across components without explicitly passing props.",
  },
];

const TestReact = () => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [userAnswers, setUserAnswers] = useState([]);

  const handleAnswerOptionClick = (option, questionIndex) => {
    setUserAnswers([
      ...userAnswers,
      { questionIndex, selected: option, correct: option.isCorrect },
    ]);
    if (option.isCorrect) setScore(score + 1);

    const next = currentQuestionIndex + 1;
    if (next < quizQuestions.length) setCurrentQuestionIndex(next);
    else setShowSubmitForm(true); // <-- show name/email form
  };

  const resetQuiz = () => {
    setCurrentQuestionIndex(0);
    setScore(0);
    setShowSubmitForm(false);
    setUserAnswers([]);
  };

  const renderDifficultyBadge = (index) => {
    if (index < 10) return <Badge colorScheme="green">Easy</Badge>;
    if (index < 30) return <Badge colorScheme="yellow">Medium</Badge>;
    return <Badge colorScheme="red">Hard</Badge>;
  };

  return (
    <Container maxW="container.lg" py={10}>
      <VStack spacing={8} align="stretch">
        <Heading as="h1" size="2xl" textAlign="center">
          React.js Knowledge Test 🤔
        </Heading>

        {showSubmitForm ? (
          <SendTestResults
            score={score}
            total={quizQuestions.length}
            userAnswers={userAnswers}
            quizQuestions={quizQuestions}
            onRetake={resetQuiz}
          />
        ) : (
          <Box p={6} shadow="md" borderWidth="1px" borderRadius="lg" bg="white">
            <VStack spacing={4} align="stretch">
              <Box>
                <HStack justify="space-between">
                  <Text fontSize="lg" fontWeight="bold">
                    Question {currentQuestionIndex + 1}/{quizQuestions.length}
                  </Text>
                  {renderDifficultyBadge(currentQuestionIndex)}
                </HStack>
                <Progress
                  value={(currentQuestionIndex / quizQuestions.length) * 100}
                  size="md"
                  colorScheme="blue"
                  my={2}
                />
                <Heading as="h3" size="lg" mt={4} mb={6}>
                  {quizQuestions[currentQuestionIndex].questionText}
                </Heading>
                {quizQuestions[currentQuestionIndex].codeSnippet && (
                  <Code p={4} display="block" whiteSpace="pre-wrap" mb={6}>
                    {quizQuestions[currentQuestionIndex].codeSnippet}
                  </Code>
                )}
              </Box>

              <VStack spacing={3} align="stretch">
                {quizQuestions[currentQuestionIndex].answerOptions.map(
                  (option, index) => (
                    <Button
                      key={index}
                      size="lg"
                      height="auto"
                      py={4}
                      onClick={() =>
                        handleAnswerOptionClick(option, currentQuestionIndex)
                      }
                      variant="outline"
                      colorScheme="gray"
                      _hover={{ bg: "gray.100" }}
                      textAlign="left"
                      whiteSpace="normal"
                    >
                      {option.answerText}
                    </Button>
                  )
                )}
              </VStack>
            </VStack>
          </Box>
        )}
      </VStack>
    </Container>
  );
};

export default TestReact;
